import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "apis";
import AdminInformationSection from "components/features/profile/AdminInformationSection";
import UnsavedChangesModal from "components/features/profile/UnsavedChangesModal";
import { DashboardLayout } from "components/layouts";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import { AdminProfileUpdateDto } from "libs/cellera-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  adminProfileSchema,
  AdminProfileFormData,
} from "validations/adminSchemas";

function UserProfilePage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const initialFormDataRef = useRef<AdminProfileFormData | null>(null);

  // Memoize form configuration to prevent unnecessary re-renders
  const formConfig = useMemo(
    () => ({
      resolver: yupResolver(adminProfileSchema),
      defaultValues: {
        id: "",
        email: "",
        avatar: null,
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
      },
      mode: "onChange" as const,
    }),
    [],
  );

  const adminProfileForm = useForm(formConfig);

  // Fetch admin profile using useQuery
  const { data: profileData, isLoading } = useQuery({
    queryKey: ["admin-profile", user?.uid],
    queryFn: async () => {
      return await adminApi.getAdminProfile();
    },
    enabled: Boolean(user?.uid),
    retry: false,
  });

  // Update admin profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: AdminProfileUpdateDto) => {
      return await adminApi.updateAdminProfile({
        adminProfileUpdateDto: profileData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-profile"] });
      showSuccess(
        "Profile Updated",
        "Your profile has been updated successfully.",
      );
    },
    onError: (error) => {
      console.error("Error updating admin profile:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update profile. Please try again.";
      showError("Update Failed", errorMessage);
    },
  });

  // Function to compare form data and detect changes
  const checkForChanges = useCallback((currentData: AdminProfileFormData) => {
    if (!initialFormDataRef.current) return false;

    const initial = initialFormDataRef.current;

    return (
      initial.firstName !== currentData.firstName ||
      initial.middleName !== currentData.middleName ||
      initial.lastName !== currentData.lastName ||
      initial.phone !== currentData.phone ||
      initial.avatar !== currentData.avatar
    );
  }, []);

  // Watch for form changes
  const watchedValues = adminProfileForm.watch();

  useEffect(() => {
    if (initialFormDataRef.current) {
      const hasChanges = checkForChanges(watchedValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [watchedValues, checkForChanges]);

  // Load profile data from TanStack Query
  useEffect(() => {
    if (profileData) {
      const formData: AdminProfileFormData = {
        id: profileData.id,
        email: profileData.email,
        avatar: profileData.avatar || null,
        firstName: profileData.firstName,
        middleName: profileData.middleName || "",
        lastName: profileData.lastName,
        phone: profileData.phone || "",
      };

      adminProfileForm.reset(formData);
      initialFormDataRef.current = formData;
      setCurrentAvatarUrl(profileData.avatar ?? null);
      setHasUnsavedChanges(false);
    } else {
      const emptyFormData: AdminProfileFormData = {
        id: "",
        email: "",
        avatar: null,
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
      };
      adminProfileForm.reset(emptyFormData);
      initialFormDataRef.current = null;
      setCurrentAvatarUrl(null);
      setHasUnsavedChanges(false);
    }
  }, [profileData, adminProfileForm]);

  // Navigation blocking
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Modern browsers ignore the return value, but we set it for compatibility
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e as any).returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const submitProfile = useCallback(
    async (
      data: AdminProfileFormData,
      currentImageUrl: string | null = null,
    ) => {
      let avatarUrl = currentImageUrl;

      // Handle profile image upload if it's a File object
      if (data.avatar instanceof File) {
        try {
          avatarUrl = await FirebaseStorageService.uploadProfileImage(
            data.avatar,
          );
        } catch {
          throw new Error("Failed to upload profile image. Please try again.");
        }
      } else if (typeof data.avatar === "string") {
        // If it's already a string URL, use it
        avatarUrl = data.avatar;
      }

      await updateProfileMutation.mutateAsync({
        firstName: data.firstName,
        middleName: data.middleName ?? undefined,
        lastName: data.lastName,
        phone: data.phone ?? undefined,
        avatar: avatarUrl ?? undefined,
      });
    },
    [updateProfileMutation],
  );

  const handleSaveAndGo = useCallback(async () => {
    try {
      const currentData = adminProfileForm.getValues();
      await submitProfile(currentData, currentAvatarUrl ?? null);
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
    } catch (err) {
      // Error handling is done in mutation
    }
  }, [adminProfileForm, currentAvatarUrl, submitProfile]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedModal(false);
  }, []);

  const onSubmit = useCallback(
    async (data: AdminProfileFormData) => {
      try {
        await submitProfile(data, currentAvatarUrl ?? null);
        // Update initial form data and reset unsaved changes
        initialFormDataRef.current = data;
        setHasUnsavedChanges(false);
      } catch {
        // Error handling is done in mutation
      }
    },
    [submitProfile, currentAvatarUrl],
  );

  const handleCancelClick = useCallback(() => {
    adminProfileForm.reset();
  }, [adminProfileForm]);

  if (isLoading) {
    return (
      <DashboardLayout title="Profile">
        <Box className="flex justify-center items-center h-64">
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profile">
      <FormProvider {...adminProfileForm}>
        <form
          onSubmit={adminProfileForm.handleSubmit(onSubmit)}
          className="relative"
        >
          <AdminInformationSection avatar={profileData?.avatar ?? ""} />
          <Box
            className="flex justify-center gap-4 mt-8"
            sx={{ position: "relative", zIndex: 10 }}
          >
            <Button
              type="submit"
              variant="contained"
              className="px-6 py-2 rounded-full w-full"
              disabled={updateProfileMutation.isPending || !hasUnsavedChanges}
              startIcon={
                updateProfileMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {updateProfileMutation.isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              className="px-6 py-2 rounded-full w-full"
              onClick={handleCancelClick}
              disabled={updateProfileMutation.isPending}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </FormProvider>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onCancel={handleCancelNavigation}
        onSaveAndGo={handleSaveAndGo}
        isSubmitting={updateProfileMutation.isPending}
      />
    </DashboardLayout>
  );
}

export default UserProfilePage;
